import React, { useMemo, useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart, BarChart,
} from 'recharts';
import KpiCard from './KpiCard';
import MultiCombobox, { ComboOption } from './MultiCombobox';
import { theme, fmtNum } from './theme';
import { WarehouseRow } from './useWarehouseRows';
import { useAliases } from './useAliases';
import { EnrichedContract } from './OperationsDashboard';
import { api } from './apiClient';

/* ============================================================================
 * SALES DASHBOARD — CEO-GRADE INTELLIGENCE PLATFORM
 * ──────────────────────────────────────────────────
 * Advanced sales intelligence with:
 *   • 4-level drill-out (Warehouse → Area → Pallet Type → Bin)
 *   • Real 12-month customer progress from WeeklyClientEntries
 *   • Bidirectional SQM ↔ Pallet converter with unit economics
 *   • Customer lifecycle (Active / Terminated / Non-Renewal / Future Space)
 *   • AI scoring + visit recommendations
 *   • Future space capacity forecasting
 *
 * All KPIs come from the SAME engine as Production — numbers always match.
 * ========================================================================== */

const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) : 0);
const EXCLUDED_WAREHOUSES = new Set(['LFS']);

// Pallet dimensions (L×W meters)
const PALLET_DB: Record<string, { l: number; w: number; label: string; h: number }> = {
  EURO:    { l: 1.2,  w: 0.8,  h: 1.8, label: 'Euro (1200×800mm)' },
  ISO:     { l: 1.2,  w: 1.0,  h: 1.8, label: 'ISO (1200×1000mm)' },
  US:      { l: 1.22, w: 1.02, h: 1.8, label: 'US Std (48×40in)' },
  HALF:    { l: 0.8,  w: 0.6,  h: 1.5, label: 'Half (800×600mm)' },
  QUARTER: { l: 0.6,  w: 0.4,  h: 1.2, label: 'Quarter (600×400mm)' },
};

type CustStatus = 'active' | 'at_risk' | 'non_renewal' | 'terminated' | 'future_space';
const STATUS_META: Record<CustStatus, { label: string; color: string; icon: string }> = {
  active:      { label: 'Active',       color: '#10b981', icon: '●' },
  at_risk:     { label: 'At Risk',      color: '#f59e0b', icon: '▲' },
  non_renewal: { label: 'Non-Renewal',  color: '#ef4444', icon: '■' },
  terminated:  { label: 'Terminated',   color: '#94a3b8', icon: '✕' },
  future_space:{ label: 'Future Space', color: '#7c3aed', icon: '◆' },
};

// ── Types ────────────────────────────────────────────────────────────────
type Kpis = {
  totalLocations: number; totalCapacity: number; usedCapacity: number;
  freeCapacity: number; sellableCapacity: number;
  committedCapacity: number; shortfallCapacity: number;
};

type DrillStack = { level: number; key: string; label: string }[];

type CustomerSummary = {
  customer: string; totalPallets: number; committed: number; shortfall: number;
  warehouses: string[]; storageTypes: string[]; utilization: number;
  score: number; recommendation: string;
  visitPriority: 'high' | 'medium' | 'low';
  status: CustStatus;
  trend: number; // +/- pallet change over last 4 weeks
  sqm: number;   // estimated SQM footprint
};

export type SalesProps = {
  kpis: Kpis; totalBins: number; warehouseData: any[];
  enrichedContracts: EnrichedContract[];
  customerStorage: any[]; weeklyEntries: any[];
  rows: WarehouseRow[]; loading: boolean;
};

/* ============================================================================ */
const SalesDashboard: React.FC<SalesProps> = ({
  kpis, totalBins, warehouseData, enrichedContracts,
  customerStorage, weeklyEntries, rows, loading,
}) => {
  const { resolve: aliasR, aliases } = useAliases();

  // ── State ──────────────────────────────────────────────────────────────
  const [customerFilter, setCustomerFilter] = useState<string[]>([]);
  const [warehouseFilter, setWarehouseFilter] = useState<string[]>([]);
  const [areaFilter, setAreaFilter] = useState<string[]>([]);
  const [palletTypeFilter, setPalletTypeFilter] = useState<string[]>([]);
  const [drill, setDrill] = useState<DrillStack>([]);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [calcMode, setCalcMode] = useState<'sqm2pallet' | 'pallet2sqm'>('sqm2pallet');
  const [calcInput, setCalcInput] = useState(1000);
  const [calcPalletType, setCalcPalletType] = useState('EURO');
  const [statusOverrides, setStatusOverrides] = useState<Record<string, CustStatus>>({});
  const [nameOverrides, setNameOverrides] = useState<Record<string, string>>({});
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editNameVal, setEditNameVal] = useState('');
  const [showAllClients, setShowAllClients] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const [customerActivity, setCustomerActivity] = useState<Record<string, any>>({});
  const [activityLoading, setActivityLoading] = useState(false);

  // ── Fetch customer activity (inbound/outbound) on mount ──────────────
  useEffect(() => {
    let cancelled = false;
    const fetchActivity = async () => {
      setActivityLoading(true);
      try {
        const res = await api.get('/api/customer-activity');
        if (!cancelled && res.data?.data) {
          const map: Record<string, any> = {};
          res.data.data.forEach((c: any) => { map[c.customer] = c; });
          setCustomerActivity(map);
        }
      } catch (e) { /* silently ignore — activity is supplementary */ }
      if (!cancelled) setActivityLoading(false);
    };
    fetchActivity();
    const interval = setInterval(fetchActivity, 5 * 60 * 1000); // refresh every 5 min
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Helper: resolve display name (local override → saved alias → original code)
  const displayName = useCallback((key: string) => {
    if (nameOverrides[key]) return nameOverrides[key];
    const alias = aliasR('customer', key);
    return alias || key;
  }, [nameOverrides, aliasR]);
  const startRename = useCallback((key: string) => {
    setEditingName(key);
    setEditNameVal(nameOverrides[key] || key);
  }, [nameOverrides]);
  const commitRename = useCallback(() => {
    if (editingName) {
      const trimmed = editNameVal.trim();
      if (trimmed && trimmed !== editingName) {
        setNameOverrides(prev => ({ ...prev, [editingName]: trimmed }));
      } else if (trimmed === editingName) {
        // Reset to original
        setNameOverrides(prev => { const n = { ...prev }; delete n[editingName]; return n; });
      }
    }
    setEditingName(null);
  }, [editingName, editNameVal]);

  // ── Warehouse / Area / Pallet Type options (from rows) ────────────────
  const warehouseOptions: ComboOption[] = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => { if (r.WAREHOUSE && !EXCLUDED_WAREHOUSES.has(r.WAREHOUSE)) set.add(r.WAREHOUSE); });
    // Include manual-only warehouses from customer storage (e.g. RUH_M)
    (customerStorage || []).forEach((r: any) => {
      const wh = String(r.WAREHOUSE || r.warehouse || '').trim();
      if (wh && !EXCLUDED_WAREHOUSES.has(wh)) set.add(wh);
    });
    return Array.from(set).sort().map(w => ({ value: w, label: aliasR('warehouse', w) }));
  }, [rows, customerStorage, aliasR]);

  const areaOptions: ComboOption[] = useMemo(() => {
    const scope = warehouseFilter.length ? rows.filter(r => warehouseFilter.includes(r.WAREHOUSE)) : rows;
    const set = new Map<string, string>();
    scope.forEach(r => {
      if (!r.STORAGE_AREA || EXCLUDED_WAREHOUSES.has(r.WAREHOUSE)) return;
      const ck = `${r.WAREHOUSE}-${r.STORAGE_AREA}`;
      if (!set.has(ck)) {
        const areaLabel = aliasR('area', r.STORAGE_AREA);
        const lbl = warehouseFilter.length === 1 ? areaLabel : `${aliasR('warehouse', r.WAREHOUSE)} · ${areaLabel}`;
        set.set(ck, lbl);
      }
    });
    return Array.from(set.entries()).map(([v, l]) => ({ value: v, label: l })).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows, warehouseFilter, aliasR]);

  const shelfSizeOptions: ComboOption[] = useMemo(() => {
    let scope = rows;
    if (warehouseFilter.length) scope = scope.filter(r => warehouseFilter.includes(r.WAREHOUSE));
    if (areaFilter.length) scope = scope.filter(r => areaFilter.includes(`${r.WAREHOUSE}-${r.STORAGE_AREA}`));
    const set = new Set<string>();
    scope.forEach(r => { if (r.SHELF_SIZE) set.add(r.SHELF_SIZE); });
    return Array.from(set).sort().map(v => ({ value: v, label: aliasR('palletType', v) || v }));
  }, [rows, warehouseFilter, areaFilter, aliasR]);

  // ── Filtered KPIs — recalculate when any filter is active ──────────────
  // IMPORTANT: Must match Operations dashboard logic exactly.
  // - totalLocations = bin count (not warehouse count)
  // - totalCapacity  = rackCap + manualCap + areaBlocked (from warehouseData)
  // - usedCapacity   = includes blocked, virtual, manual, floor, shortfall
  const hasFilters = warehouseFilter.length > 0 || areaFilter.length > 0 || palletTypeFilter.length > 0;
  const filteredKpis = useMemo((): Kpis => {
    if (!hasFilters) {
      // No filters → derive from warehouseData (same source Operations cards use)
      // so Commercial and Operations KPI cards always show identical numbers.
      const totalLocations = warehouseData.reduce((s, w) => s + (w.totalBin || 0), 0);
      const totalCapacity = warehouseData.reduce((s, w) => s + w.totalCap, 0);
      const usedCapacity = warehouseData.reduce((s, w) => s + w.usedCap, 0);
      const sellableCapacity = warehouseData.reduce((s, w) => s + w.sellable, 0);
      return {
        totalLocations, totalCapacity, usedCapacity,
        freeCapacity: sellableCapacity, sellableCapacity,
        committedCapacity: kpis.committedCapacity,
        shortfallCapacity: kpis.shortfallCapacity,
      };
    }

    // Warehouse-only or warehouse+pallet filter (no area) → derive from
    // warehouseData. This ensures manualCap, areaBlocked, virtual, floor,
    // shortfall are all included (with proportional distribution for pallet
    // filter), matching Operations dashboard KPI cards.
    const noAreaFilter = !areaFilter.length;
    if (noAreaFilter) {
      let filtered = warehouseData;
      if (warehouseFilter.length) {
        const whSet = new Set(warehouseFilter);
        filtered = warehouseData.filter(w => whSet.has(w.warehouse));
      }
      const totalLocations = filtered.reduce((s, w) => s + (w.totalBin || 0), 0);
      const totalCapacity = filtered.reduce((s, w) => s + w.totalCap, 0);
      const usedCapacity = filtered.reduce((s, w) => s + w.usedCap, 0);
      const sellableCapacity = filtered.reduce((s, w) => s + w.sellable, 0);
      const freeCapacity = sellableCapacity;
      const committedCapacity = filtered.reduce((s, w) => s + (w.committed || 0), 0);
      const shortfallCapacity = committedCapacity;
      return { totalLocations, totalCapacity, usedCapacity, freeCapacity, sellableCapacity, committedCapacity, shortfallCapacity };
    }

    // Area filter (with or without pallet-type) → use row-level data for
    // area granularity, count rows for totalLocations.
    let scope = rows.filter(r => !EXCLUDED_WAREHOUSES.has(r.WAREHOUSE));
    if (warehouseFilter.length) scope = scope.filter(r => warehouseFilter.includes(r.WAREHOUSE));
    if (areaFilter.length) scope = scope.filter(r => areaFilter.includes(`${r.WAREHOUSE}-${r.STORAGE_AREA}`));
    if (palletTypeFilter.length) scope = scope.filter(r => palletTypeFilter.includes(r.SHELF_SIZE));

    const totalLocations = scope.length;   // each row = one bin location
    const totalCapacity = scope.reduce((s, r) => s + r.TOTAL_BINS, 0);
    const usedCapacity = scope.reduce((s, r) => s + r.USED_BINS, 0);
    const freeCapacity = scope.reduce((s, r) => s + r.FREE_BINS, 0);
    const sellableCapacity = Math.max(0, totalCapacity - usedCapacity);

    return { totalLocations, totalCapacity, usedCapacity, freeCapacity, sellableCapacity, committedCapacity: 0, shortfallCapacity: 0 };
  }, [hasFilters, kpis, rows, warehouseData, warehouseFilter, areaFilter, palletTypeFilter, enrichedContracts]);

  // ── Parse customer rows ────────────────────────────────────────────────
  const customerRows = useMemo(() =>
    (customerStorage || []).map((r: any) => ({
      customer: String(r.CUSTOMER ?? r.customer ?? '').trim(),
      warehouse: String(r.WAREHOUSE ?? r.warehouse ?? '').trim(),
      storageArea: String(r.STORAGE_LOCATION ?? r.storage_location ?? r.STORAGE_AREA ?? '').trim(),
      usedPallets: num(r.USED_PALLETS ?? r.used_pallets),
      storageType: String(r.STORAGE_TYPE ?? r.storage_type ?? 'Rack').trim().toUpperCase(),
    })).filter((r: any) => r.customer && !EXCLUDED_WAREHOUSES.has(r.warehouse)),
  [customerStorage]);

  // ── Filtered customer rows (respects warehouse + area + pallet type) ──
  const impliedWarehouses = useMemo(() => {
    if (warehouseFilter.length) return new Set(warehouseFilter);
    if (areaFilter.length) {
      const whs = new Set<string>();
      areaFilter.forEach(ck => { const idx = ck.indexOf('-'); if (idx > 0) whs.add(ck.substring(0, idx)); });
      return whs;
    }
    return null;
  }, [warehouseFilter, areaFilter]);

  // When pallet type is selected, find which warehouse+area combos have that pallet type
  const palletTypeWarehouses = useMemo(() => {
    if (!palletTypeFilter.length) return null;
    const whSet = new Set<string>();
    rows.filter(r => palletTypeFilter.includes(r.SHELF_SIZE) && !EXCLUDED_WAREHOUSES.has(r.WAREHOUSE))
      .forEach(r => whSet.add(r.WAREHOUSE));
    return whSet;
  }, [palletTypeFilter, rows]);

  // ── Customer dropdown: only customers matching current warehouse/area/palletType filters
  const customerOptions: ComboOption[] = useMemo(() => {
    let source = customerRows;
    if (impliedWarehouses) source = source.filter((r: any) => impliedWarehouses.has(r.warehouse));
    if (areaFilter.length) {
      const areaSet = new Set(areaFilter);
      source = source.filter((r: any) => {
        if (!r.storageArea) return impliedWarehouses ? impliedWarehouses.has(r.warehouse) : true;
        return areaSet.has(`${r.warehouse}-${r.storageArea}`);
      });
    }
    if (palletTypeWarehouses) source = source.filter((r: any) => palletTypeWarehouses.has(r.warehouse));
    const set = new Set<string>();
    source.forEach((r: any) => set.add(r.customer));
    return Array.from(set).sort().map(c => ({ value: c, label: displayName(c) }));
  }, [customerRows, displayName, impliedWarehouses, areaFilter, palletTypeWarehouses]);

  const filteredCustRows = useMemo(() => {
    let out = customerRows;
    if (customerFilter.length) out = out.filter((r: any) => customerFilter.includes(r.customer));
    if (impliedWarehouses) out = out.filter((r: any) => impliedWarehouses.has(r.warehouse));
    // Area filter: match warehouse-storageArea composite key
    if (areaFilter.length) {
      const areaSet = new Set(areaFilter);
      out = out.filter((r: any) => {
        if (!r.storageArea) return impliedWarehouses ? impliedWarehouses.has(r.warehouse) : true;
        return areaSet.has(`${r.warehouse}-${r.storageArea}`);
      });
    }
    // Pallet type filter: only customers in warehouses that have those pallet types
    if (palletTypeWarehouses) out = out.filter((r: any) => palletTypeWarehouses.has(r.warehouse));
    return out;
  }, [customerRows, customerFilter, impliedWarehouses, areaFilter, palletTypeWarehouses]);

  // ── 12-month real trend from WeeklyClientEntries ───────────────────────
  const monthlyTrend = useMemo(() => {
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const now = new Date();
    // Initialize 12 months
    const months: Record<string, { capacity: number; used: number; clients: Set<string>; entries: number }> = {};
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months[`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`] = { capacity: 0, used: 0, clients: new Set(), entries: 0 };
    }
    (weeklyEntries || []).forEach((e: any) => {
      const wh = String(e.warehouse || '').trim();
      if (EXCLUDED_WAREHOUSES.has(wh)) return;
      // Respect warehouse filter
      if (warehouseFilter.length && !warehouseFilter.includes(wh)) return;
      // Respect customer filter on weekly entries
      if (customerFilter.length) {
        const client = String(e.client_name || '').trim();
        if (!customerFilter.includes(client)) return;
      }
      const year = num(e.year); const week = num(e.week_number);
      if (!year || !week) return;
      const month = Math.min(12, Math.max(1, Math.ceil(week / 4.33)));
      const key = `${year}-${String(month).padStart(2,'0')}`;
      if (months[key]) {
        months[key].capacity += num(e.area_capacity || e.capacity || 0);
        months[key].used += num(e.closing_balance || e.balance || 0);
        months[key].clients.add(String(e.client_name || '').trim());
        months[key].entries += 1;
      }
    });
    return Object.entries(months).sort(([a],[b]) => a.localeCompare(b)).map(([key, v]) => {
      const [y, m] = key.split('-');
      return {
        month: `${monthNames[parseInt(m)-1]} ${y.slice(2)}`,
        capacity: v.entries > 0 ? v.capacity : 0,
        used: v.entries > 0 ? v.used : 0,
        sellable: v.entries > 0 ? Math.max(0, v.capacity - v.used) : 0,
        clients: v.clients.size,
        util: v.capacity > 0 ? pct(v.used, v.capacity) : 0,
      };
    });
  }, [weeklyEntries, warehouseFilter, customerFilter]);

  // ── Customer summaries with AI scoring & lifecycle ─────────────────────
  const customerSummaries: CustomerSummary[] = useMemo(() => {
    const custMap: Record<string, { pallets: number; warehouses: Set<string>; storageTypes: Set<string> }> = {};
    filteredCustRows.forEach((r: any) => {
      if (!custMap[r.customer]) custMap[r.customer] = { pallets: 0, warehouses: new Set(), storageTypes: new Set() };
      const c = custMap[r.customer];
      c.pallets += r.usedPallets;
      c.warehouses.add(r.warehouse);
      c.storageTypes.add(r.storageType === 'MANUAL' ? 'Manual' : r.storageType === 'BULK' || r.storageType === 'FLOOR' ? 'Bulk' : 'Rack');
    });

    // Commitment from enrichedContracts
    const commitMap: Record<string, { committed: number; shortfall: number }> = {};
    enrichedContracts.forEach(c => {
      if (!commitMap[c.customer]) commitMap[c.customer] = { committed: 0, shortfall: 0 };
      commitMap[c.customer].committed += c.minPallets;
      commitMap[c.customer].shortfall += c.shortfall;
    });

    // Trend: last 4 weeks change per customer
    const trendMap: Record<string, number[]> = {};
    const sortedWeekly = [...(weeklyEntries || [])].sort((a: any, b: any) => (num(b.year) - num(a.year)) || (num(b.week_number) - num(a.week_number)));
    sortedWeekly.forEach((e: any) => {
      const cust = String(e.client_name || '').trim();
      if (!cust) return;
      if (!trendMap[cust]) trendMap[cust] = [];
      if (trendMap[cust].length < 5) trendMap[cust].push(num(e.closing_balance || e.balance || 0));
    });

    return Object.entries(custMap).map(([customer, data]) => {
      const commit = commitMap[customer] || { committed: 0, shortfall: 0 };
      const utilization = commit.committed > 0 ? pct(data.pallets, commit.committed) : 0;
      const shortfall = Math.max(0, commit.committed - data.pallets);

      // Trend: difference between newest and oldest in last 5 weeks
      const weeks = trendMap[customer] || [];
      const trend = weeks.length >= 2 ? weeks[0] - weeks[weeks.length - 1] : 0;

      // SQM estimate (assuming EURO pallet footprint)
      const sqm = Math.round(data.pallets * 1.2 * 0.8 * 10) / 10;

      // AI scoring
      let score = 50;
      if (commit.committed > 0 && utilization < 50) score += 30;
      else if (commit.committed > 0 && utilization < 80) score += 15;
      if (data.pallets > 1000) score += 10;
      if (data.pallets > 5000) score += 10;
      if (shortfall > 0) score += 20;
      if (commit.committed === 0 && data.pallets > 100) score += 15;
      if (trend < -50) score += 10; // declining = needs attention
      score = Math.min(100, score);

      // Status detection
      const overrideStatus = statusOverrides[customer];
      let status: CustStatus = overrideStatus || 'active';
      if (!overrideStatus) {
        if (data.pallets === 0) status = 'terminated';
        else if (trend < -100 || (commit.committed > 0 && utilization < 30)) status = 'at_risk';
        else if (shortfall > commit.committed * 0.5 && commit.committed > 0) status = 'at_risk';
      }

      let recommendation = '';
      let visitPriority: 'high' | 'medium' | 'low' = 'low';
      if (score >= 75) {
        visitPriority = 'high';
        if (shortfall > 0) recommendation = `${fmtNum(shortfall)} pallet shortfall (${(100-utilization)}% gap). Urgent: discuss ramp-up timeline and capacity reservation.`;
        else if (commit.committed === 0 && data.pallets > 100) recommendation = `${fmtNum(data.pallets)} pallets, NO contract. Convert to minimum commitment — est. ${fmtNum(sqm)} SQM value.`;
        else if (utilization < 50) recommendation = `Only ${utilization}% utilized. Review demand forecast, consider contract renegotiation or shared space model.`;
        else if (trend < -50) recommendation = `Declining ${Math.abs(trend)} pallets/month. Investigate root cause — possible churning client.`;
        else recommendation = `Strategic account. Explore capacity expansion, value-added services, long-term commitment renewal.`;
      } else if (score >= 50) {
        visitPriority = 'medium';
        recommendation = utilization < 80 && commit.committed > 0
          ? `${utilization}% utilization. Moderate gap — quarterly review to check seasonal patterns.`
          : `Stable at ${fmtNum(data.pallets)} pallets (${fmtNum(sqm)} SQM). Standard retention review.`;
      } else {
        recommendation = `Stable account (${fmtNum(data.pallets)} pallets). Monitor quarterly.`;
      }

      return {
        customer, totalPallets: data.pallets, committed: commit.committed, shortfall,
        warehouses: Array.from(data.warehouses), storageTypes: Array.from(data.storageTypes),
        utilization, score, recommendation, visitPriority, status, trend, sqm,
      };
    }).sort((a, b) => b.score - a.score);
  }, [filteredCustRows, enrichedContracts, weeklyEntries, statusOverrides]);

  // ── Pie data ───────────────────────────────────────────────────────────
  const customerPieData = useMemo(() => {
    const top = customerSummaries.slice(0, 7);
    const rest = customerSummaries.slice(7).reduce((s, c) => s + c.totalPallets, 0);
    const data = top.map(c => ({ name: displayName(c.customer), value: c.totalPallets }));
    if (rest > 0) data.push({ name: 'Others', value: rest });
    return data;
  }, [customerSummaries, displayName]);

  // ── SQM ↔ Pallet Calculator ────────────────────────────────────────────
  const calcResult = useMemo(() => {
    const p = PALLET_DB[calcPalletType] || PALLET_DB.EURO;
    const sqmPer = p.l * p.w;
    const sqftPer = sqmPer * 10.7639;
    if (calcMode === 'sqm2pallet') {
      const pallets = Math.floor(calcInput / sqmPer);
      return { pallets, sqm: calcInput, sqft: calcInput * 10.7639, sqmPer, sqftPer, cbm: calcInput * p.h };
    } else {
      const sqm = Math.round(calcInput * sqmPer * 10) / 10;
      return { pallets: calcInput, sqm, sqft: sqm * 10.7639, sqmPer, sqftPer, cbm: sqm * p.h };
    }
  }, [calcMode, calcInput, calcPalletType]);

  // ── Drill-out data ─────────────────────────────────────────────────────
  const drillData = useMemo(() => {
    if (drill.length === 0) return null;
    const level = drill.length;

    if (level === 1) {
      // L0 → Show warehouses (respect filters)
      let whData = warehouseData;
      if (warehouseFilter.length) whData = whData.filter((w: any) => warehouseFilter.includes(w.warehouse));
      return whData.map((w: any) => ({
        key: w.warehouse, label: aliasR('warehouse', w.warehouse),
        capacity: w.totalCap, used: w.usedCap, sellable: w.sellable,
        util: w.util, committed: w.committed, bins: w.totalBin,
      }));
    }
    if (level === 2) {
      // L1 → Areas within selected warehouse
      const wh = drill[1]?.key || '';
      const areaMap: Record<string, { cap: number; used: number; bins: number }> = {};
      rows.filter(r => r.WAREHOUSE === wh && !EXCLUDED_WAREHOUSES.has(r.WAREHOUSE)).forEach(r => {
        const area = r.STORAGE_AREA || 'Unknown';
        if (!areaMap[area]) areaMap[area] = { cap: 0, used: 0, bins: 0 };
        areaMap[area].cap += r.TOTAL_BINS;
        areaMap[area].used += r.USED_BINS;
        areaMap[area].bins += 1;
      });
      return Object.entries(areaMap).map(([area, v]) => ({
        key: area, label: aliasR('area', area),
        capacity: v.cap, used: v.used, sellable: Math.max(0, v.cap - v.used),
        util: pct(v.used, v.cap), bins: v.bins, committed: 0,
      })).sort((a, b) => b.capacity - a.capacity);
    }
    if (level === 3) {
      // L2 → Pallet types within selected area
      const wh = drill[1]?.key || '';
      const area = drill[2]?.key || '';
      const ptMap: Record<string, { cap: number; used: number; bins: number }> = {};
      rows.filter(r => r.WAREHOUSE === wh && r.STORAGE_AREA === area).forEach(r => {
        const pt = r.SHELF_SIZE || 'Unknown';
        if (!ptMap[pt]) ptMap[pt] = { cap: 0, used: 0, bins: 0 };
        ptMap[pt].cap += r.TOTAL_BINS;
        ptMap[pt].used += r.USED_BINS;
        ptMap[pt].bins += 1;
      });
      return Object.entries(ptMap).map(([pt, v]) => ({
        key: pt, label: aliasR('palletType', pt) || pt,
        capacity: v.cap, used: v.used, sellable: Math.max(0, v.cap - v.used),
        util: pct(v.used, v.cap), bins: v.bins, committed: 0,
      })).sort((a, b) => b.capacity - a.capacity);
    }
    if (level === 4) {
      // L3 → Individual bins
      const wh = drill[1]?.key || '';
      const area = drill[2]?.key || '';
      const pt = drill[3]?.key || '';
      return rows.filter(r => r.WAREHOUSE === wh && r.STORAGE_AREA === area && r.SHELF_SIZE === pt)
        .slice(0, 100).map(r => ({
          key: r.BIN_LOCATION, label: r.BIN_LOCATION,
          capacity: r.TOTAL_BINS, used: r.USED_BINS, sellable: r.FREE_BINS,
          util: r.TOTAL_BINS > 0 ? pct(r.USED_BINS, r.TOTAL_BINS) : 0,
          bins: 1, committed: 0, blocked: r.BLOCKED,
        }));
    }
    return null;
  }, [drill, warehouseData, rows, aliasR, warehouseFilter]);

  const drillDown = useCallback((key: string, label: string) => {
    setDrill(prev => [...prev, { level: prev.length + 1, key, label }]);
  }, []);

  const drillUp = useCallback((toLevel: number) => {
    setDrill(prev => prev.slice(0, toLevel));
  }, []);

  const P = theme.palette;
  const PIE_PALETTE = [P.occupied, P.sellable, P.committed, '#7c3aed', '#0891b2', '#db2777', '#a16207', '#94a3b8'];
  const PRIORITY_COLORS: Record<string, string> = { high: '#ef4444', medium: '#f59e0b', low: '#10b981' };
  const utilPct = filteredKpis.totalCapacity > 0 ? Math.round((filteredKpis.usedCapacity / filteredKpis.totalCapacity) * 100) : 0;

  // ── Status counts for hero ──────────────────────────────────────────────
  const statusCounts = useMemo(() => {
    const counts: Record<CustStatus, number> = { active: 0, at_risk: 0, non_renewal: 0, terminated: 0, future_space: 0 };
    customerSummaries.forEach(c => { counts[c.status] = (counts[c.status] || 0) + 1; });
    return counts;
  }, [customerSummaries]);

  // ── Area category helper: chamber-icon first, then 6-prefix fallback ────
  const classifyArea = useCallback((storageArea: string, warehouse?: string): string => {
    // 1) Per-warehouse chamber alias icon decides the category
    if (warehouse) {
      const raw = aliases.area?.[`${warehouse}-${storageArea}`];
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.icon) {
            const iconToCat: Record<string, string> = {
              thermo:  'Temperature Controlled',
              cold:    'Cold Room',
              chem:    'Chemical Room',
              danger:  'Dangerous Goods',
              freezer: 'Freezer',
              box:     'Other Storage',
            };
            if (iconToCat[parsed.icon]) return iconToCat[parsed.icon];
          }
        } catch { /* not JSON */ }
      }
    }
    // 2) Prefix-based fallback (so unrenamed chambers still get a category)
    const upper = storageArea.toUpperCase();
    if (upper.startsWith('CHM') || upper.includes('TEMP'))   return 'Temperature Controlled';
    if (upper.startsWith('CLD') || upper.includes('COLD'))   return 'Cold Room';
    if (upper.startsWith('CML') || upper.includes('CHEM'))   return 'Chemical Room';
    if (upper.startsWith('DRG') || upper.includes('DANGER')) return 'Dangerous Goods';
    if (upper.startsWith('FR')  || upper.includes('FRZ') || upper.includes('FREEZE')) return 'Freezer';
    return 'Other Storage';
  }, [aliases]);

  const AREA_CAT_META: Record<string, { color: string; light: string; icon: string }> = {
    'Temperature Controlled': { color: '#a855f7', light: '#e9d5ff', icon: '🧪' },
    'Cold Room':              { color: '#0ea5e9', light: '#bae6fd', icon: '❄'  },
    'Chemical Room':          { color: '#f97316', light: '#fed7aa', icon: '⚗'  },
    'Dangerous Goods':        { color: '#ef4444', light: '#fecaca', icon: '⚠'  },
    'Freezer':                { color: '#06b6d4', light: '#a5f3fc', icon: '🧊' },
    'Other Storage':          { color: '#f59e0b', light: '#fde68a', icon: '📦' },
  };
  const CAT_ORDER = ['Temperature Controlled', 'Cold Room', 'Chemical Room', 'Dangerous Goods', 'Freezer', 'Other Storage'];

  // ── Area category breakdown (Cold / Freezer / Temp+Amb+Dry) ────────────────────
  // Uses KPI engine totals so pie sums match the 6 KPI cards exactly.
  const areaCategoryBreakdown = useMemo(() => {
    let scope = rows.filter(r => !EXCLUDED_WAREHOUSES.has(r.WAREHOUSE));
    if (warehouseFilter.length) scope = scope.filter(r => warehouseFilter.includes(r.WAREHOUSE));
    if (areaFilter.length) scope = scope.filter(r => areaFilter.includes(`${r.WAREHOUSE}-${r.STORAGE_AREA}`));
    if (palletTypeFilter.length) scope = scope.filter(r => palletTypeFilter.includes(r.SHELF_SIZE));

    const byCat: Record<string, { cap: number; used: number; free: number; bins: number; areas: Set<string> }> = {};
    scope.forEach(r => {
      const cat = classifyArea(r.STORAGE_AREA, r.WAREHOUSE);
      if (!byCat[cat]) byCat[cat] = { cap: 0, used: 0, free: 0, bins: 0, areas: new Set() };
      byCat[cat].cap += r.TOTAL_BINS;
      byCat[cat].used += r.USED_BINS;
      byCat[cat].free += r.FREE_BINS;
      byCat[cat].bins += 1;
      byCat[cat].areas.add(r.STORAGE_AREA);
    });

    // Raw totals from rows
    const rawTotalCap = Object.values(byCat).reduce((s, c) => s + c.cap, 0);
    const rawTotalUsed = Object.values(byCat).reduce((s, c) => s + c.used, 0);
    const rawTotalFree = Object.values(byCat).reduce((s, c) => s + c.free, 0);

    // KPI engine totals (authoritative)
    const kpiCap = filteredKpis.totalCapacity;
    const kpiUsed = filteredKpis.usedCapacity;
    const kpiSellable = filteredKpis.sellableCapacity;

    // No filters active → scale to match KPI exactly; with filters → use raw proportions
    const noFilters = !warehouseFilter.length && !areaFilter.length && !palletTypeFilter.length;

    return CAT_ORDER.map(cat => {
      const v = byCat[cat] || { cap: 0, used: 0, free: 0, bins: 0, areas: new Set() };
      let capacity: number, used: number, sellable: number;
      if (noFilters && rawTotalCap > 0) {
        // Distribute KPI totals proportionally by each category's share
        const capShare = v.cap / rawTotalCap;
        const usedShare = rawTotalUsed > 0 ? v.used / rawTotalUsed : 0;
        const freeShare = rawTotalFree > 0 ? v.free / rawTotalFree : 0;
        capacity = Math.round(kpiCap * capShare);
        used = Math.round(kpiUsed * usedShare);
        sellable = Math.round(kpiSellable * freeShare);
      } else {
        capacity = v.cap;
        used = v.used;
        sellable = Math.max(0, v.cap - v.used);
      }
      return {
        category: cat,
        capacity,
        used,
        sellable,
        util: capacity > 0 ? Math.round((used / capacity) * 100) : 0,
        bins: v.bins,
        areaCount: v.areas.size,
        areas: Array.from(v.areas).sort(),
      };
    }).filter(c => c.capacity > 0 || c.used > 0);
  }, [rows, warehouseFilter, areaFilter, palletTypeFilter, classifyArea, filteredKpis]);

  // ── Pallet type breakdown per category (for pie drill-down) ────────────
  const categoryPalletBreakdown = useMemo(() => {
    let scope = rows.filter(r => !EXCLUDED_WAREHOUSES.has(r.WAREHOUSE));
    if (warehouseFilter.length) scope = scope.filter(r => warehouseFilter.includes(r.WAREHOUSE));
    if (areaFilter.length) scope = scope.filter(r => areaFilter.includes(`${r.WAREHOUSE}-${r.STORAGE_AREA}`));
    if (palletTypeFilter.length) scope = scope.filter(r => palletTypeFilter.includes(r.SHELF_SIZE));

    const byCatPT: Record<string, Record<string, { used: number; free: number; cap: number }>> = {};
    scope.forEach(r => {
      const cat = classifyArea(r.STORAGE_AREA, r.WAREHOUSE);
      const pt = r.SHELF_SIZE || 'Unknown';
      if (!byCatPT[cat]) byCatPT[cat] = {};
      if (!byCatPT[cat][pt]) byCatPT[cat][pt] = { used: 0, free: 0, cap: 0 };
      byCatPT[cat][pt].used += r.USED_BINS;
      byCatPT[cat][pt].free += r.FREE_BINS;
      byCatPT[cat][pt].cap += r.TOTAL_BINS;
    });
    const result: Record<string, { palletType: string; used: number; free: number; capacity: number; util: number }[]> = {};
    Object.entries(byCatPT).forEach(([cat, pts]) => {
      result[cat] = Object.entries(pts).map(([pt, v]) => ({
        palletType: aliasR('palletType', pt),
        used: v.used,
        free: v.free,
        capacity: v.cap,
        util: v.cap > 0 ? Math.round((v.used / v.cap) * 100) : 0,
      })).sort((a, b) => b.capacity - a.capacity);
    });
    return result;
  }, [rows, warehouseFilter, areaFilter, palletTypeFilter, classifyArea, aliasR]);

  // ── Per-warehouse × category matrix (for the chart) ────────────────────
  const whCategoryData = useMemo(() => {
    let scope = rows.filter(r => !EXCLUDED_WAREHOUSES.has(r.WAREHOUSE));
    if (warehouseFilter.length) scope = scope.filter(r => warehouseFilter.includes(r.WAREHOUSE));
    if (areaFilter.length) scope = scope.filter(r => areaFilter.includes(`${r.WAREHOUSE}-${r.STORAGE_AREA}`));
    if (palletTypeFilter.length) scope = scope.filter(r => palletTypeFilter.includes(r.SHELF_SIZE));

    const byWh: Record<string, Record<string, { cap: number; used: number }>> = {};
    scope.forEach(r => {
      const wh = r.WAREHOUSE;
      const cat = classifyArea(r.STORAGE_AREA, r.WAREHOUSE);
      if (!byWh[wh]) byWh[wh] = {};
      if (!byWh[wh][cat]) byWh[wh][cat] = { cap: 0, used: 0 };
      byWh[wh][cat].cap += r.TOTAL_BINS;
      byWh[wh][cat].used += r.USED_BINS;
    });
    return Object.entries(byWh).map(([wh, cats]) => ({
      warehouse: aliasR('warehouse', wh),
      coldUsed: cats['Cold']?.used || 0, coldCap: cats['Cold']?.cap || 0, coldFree: Math.max(0, (cats['Cold']?.cap || 0) - (cats['Cold']?.used || 0)),
      freezerUsed: cats['Freezer']?.used || 0, freezerCap: cats['Freezer']?.cap || 0, freezerFree: Math.max(0, (cats['Freezer']?.cap || 0) - (cats['Freezer']?.used || 0)),
      dryUsed: cats['Temp+Amb+Dry']?.used || 0, dryCap: cats['Temp+Amb+Dry']?.cap || 0, dryFree: Math.max(0, (cats['Temp+Amb+Dry']?.cap || 0) - (cats['Temp+Amb+Dry']?.used || 0)),
      total: Object.values(cats).reduce((s, c) => s + c.cap, 0),
    })).sort((a, b) => b.total - a.total);
  }, [rows, warehouseFilter, areaFilter, palletTypeFilter, classifyArea, aliasR]);

  // ── Detailed per-area breakdown (for the drill table) ─────────────────
  const areaDetailBreakdown = useMemo(() => {
    let scope = rows.filter(r => !EXCLUDED_WAREHOUSES.has(r.WAREHOUSE));
    if (warehouseFilter.length) scope = scope.filter(r => warehouseFilter.includes(r.WAREHOUSE));
    if (areaFilter.length) scope = scope.filter(r => areaFilter.includes(`${r.WAREHOUSE}-${r.STORAGE_AREA}`));
    if (palletTypeFilter.length) scope = scope.filter(r => palletTypeFilter.includes(r.SHELF_SIZE));

    const byArea: Record<string, { cap: number; used: number; bins: number; warehouse: string; area: string }> = {};
    scope.forEach(r => {
      const ck = `${r.WAREHOUSE}-${r.STORAGE_AREA}`;
      if (!byArea[ck]) byArea[ck] = { cap: 0, used: 0, bins: 0, warehouse: r.WAREHOUSE, area: r.STORAGE_AREA };
      byArea[ck].cap += r.TOTAL_BINS;
      byArea[ck].used += r.USED_BINS;
      byArea[ck].bins += 1;
    });
    return Object.entries(byArea).map(([ck, v]) => ({
      key: ck,
      label: aliasR('area', v.area),
      warehouse: aliasR('warehouse', v.warehouse),
      rawArea: v.area,
      category: classifyArea(v.area, v.warehouse),
      capacity: v.cap,
      used: v.used,
      sellable: Math.max(0, v.cap - v.used),
      util: v.cap > 0 ? Math.round((v.used / v.cap) * 100) : 0,
      bins: v.bins,
    })).sort((a, b) => b.capacity - a.capacity);
  }, [rows, warehouseFilter, areaFilter, palletTypeFilter, aliasR, classifyArea]);

  // ── Pallet type breakdown ─────────────────────────────────────────────
  const palletTypeBreakdown = useMemo(() => {
    let scope = rows.filter(r => !EXCLUDED_WAREHOUSES.has(r.WAREHOUSE));
    if (warehouseFilter.length) scope = scope.filter(r => warehouseFilter.includes(r.WAREHOUSE));
    if (areaFilter.length) scope = scope.filter(r => areaFilter.includes(`${r.WAREHOUSE}-${r.STORAGE_AREA}`));
    if (palletTypeFilter.length) scope = scope.filter(r => palletTypeFilter.includes(r.SHELF_SIZE));

    const byType: Record<string, { cap: number; used: number; bins: number }> = {};
    scope.forEach(r => {
      const t = r.SHELF_SIZE || 'Unknown';
      if (!byType[t]) byType[t] = { cap: 0, used: 0, bins: 0 };
      byType[t].cap += r.TOTAL_BINS;
      byType[t].used += r.USED_BINS;
      byType[t].bins += 1;
    });
    return Object.entries(byType).map(([t, v]) => ({
      key: t,
      label: aliasR('palletType', t) || t,
      capacity: v.cap,
      used: v.used,
      sellable: Math.max(0, v.cap - v.used),
      util: v.cap > 0 ? Math.round((v.used / v.cap) * 100) : 0,
      bins: v.bins,
    })).sort((a, b) => b.capacity - a.capacity);
  }, [rows, warehouseFilter, areaFilter, palletTypeFilter, aliasR]);

  // ── Future space forecast ──────────────────────────────────────────────
  const futureSpace = useMemo(() => {
    const terminatedPallets = customerSummaries.filter(c => c.status === 'terminated' || c.status === 'non_renewal')
      .reduce((s, c) => s + c.totalPallets, 0);
    const atRiskPallets = customerSummaries.filter(c => c.status === 'at_risk')
      .reduce((s, c) => s + Math.round(c.totalPallets * 0.3), 0); // 30% churn risk
    const freedSqm = Math.round((terminatedPallets + atRiskPallets) * 1.2 * 0.8);
    return { terminatedPallets, atRiskPallets, totalFreed: terminatedPallets + atRiskPallets, freedSqm };
  }, [customerSummaries]);

  /* ====================================================================
   * RENDER
   * ==================================================================== */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* SVG gradients */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          {[['SOcc', P.occupied],['SSel', P.sellable],['SCom', P.committed],['SNeu', P.neutral],['SPur','#7c3aed']].map(([id, c]) => (
            <linearGradient key={id} id={`grad${id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={c} stopOpacity={1} />
              <stop offset="100%" stopColor={c} stopOpacity={0.55} />
            </linearGradient>
          ))}
        </defs>
      </svg>

      {/* ╭─── HERO ───╮ */}
      <div style={{
        position: 'relative',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 55%, #0891b2 100%)',
        borderRadius: 18, padding: '22px 28px', color: '#fff',
        boxShadow: '0 10px 30px rgba(30,58,138,0.18)',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(600px 220px at 90% -20%, rgba(255,255,255,0.18), transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.4, opacity: 0.75, fontWeight: 600 }}>Commercial · Live Intelligence</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, letterSpacing: -0.3 }}>Client Capacity & Revenue Analytics</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 3, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span>{customerSummaries.length} clients · {fmtNum(filteredKpis.totalCapacity)} total pallets</span>
              <span style={{ fontWeight: 600, color: '#d1fae5' }}>{fmtNum(filteredKpis.sellableCapacity)} sellable</span>
              {Object.entries(statusCounts).filter(([,v]) => v > 0).map(([k, v]) => (
                <span key={k} style={{ opacity: 0.9 }}>{STATUS_META[k as CustStatus].icon} {v} {STATUS_META[k as CustStatus].label}</span>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
            <HeroStat label="Occupied" value={fmtNum(filteredKpis.usedCapacity)} tint="#bfdbfe" />
            <HeroStat label="Sellable" value={fmtNum(filteredKpis.sellableCapacity)} tint="#bbf7d0" />
            <HeroStat label="Committed" value={fmtNum(filteredKpis.committedCapacity)} tint="#fde68a" />
            <HeroStat label="Shortfall" value={fmtNum(filteredKpis.shortfallCapacity)} tint="#fecaca" />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 18px', borderRadius: 14, background: 'rgba(255,255,255,0.12)' }}>
              <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.8, fontVariantNumeric: 'tabular-nums' }}>{utilPct}%</div>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.8, fontWeight: 600 }}>Utilized</div>
            </div>
          </div>
        </div>
      </div>

      {/* ╭─── FILTER BAR ───╮ */}
      <div style={{ background: '#fff', border: '1px solid #e5eaf2', borderRadius: 14, padding: '14px 22px', boxShadow: '0 1px 2px rgba(20,40,80,0.04)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 14, alignItems: 'end' }}>
          <MultiCombobox label="Warehouse" values={warehouseFilter} options={warehouseOptions}
            placeholder="All Warehouses" width="100%" onChange={v => { setWarehouseFilter(v); setAreaFilter([]); setPalletTypeFilter([]); setCustomerFilter([]); }} />
          <MultiCombobox label="Area (Cold / Temp+Amb+Dry)" values={areaFilter} options={areaOptions}
            placeholder="All Areas" width="100%" onChange={v => { setAreaFilter(v); setPalletTypeFilter([]); setCustomerFilter([]); }} />
          <MultiCombobox label="Pallet Type" values={palletTypeFilter} options={shelfSizeOptions}
            placeholder="All Pallet Types" width="100%" onChange={v => { setPalletTypeFilter(v); setCustomerFilter([]); }} />
          <MultiCombobox label="Customer" values={customerFilter} options={customerOptions}
            placeholder="All Customers" width="100%" onChange={v => setCustomerFilter(v)} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: theme.textMuted, fontWeight: 500, paddingBottom: 8, whiteSpace: 'nowrap' }}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{customerSummaries.length} clients</span>
            {(customerFilter.length + warehouseFilter.length + areaFilter.length + palletTypeFilter.length > 0) && (
              <button onClick={() => { setCustomerFilter([]); setWarehouseFilter([]); setAreaFilter([]); setPalletTypeFilter([]); }} style={clearBtnStyle}>Clear</button>
            )}
          </div>
        </div>
      </div>

      {/* ╭─── KPI CARDS (6) — Same as Production ───╮ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 12 }}>
        <KpiCard title="Total Location" subtitle="Physical rack bins" value={filteredKpis.totalLocations} unit="Bins" loading={loading} accent={P.neutral}
          onClick={() => setDrill([{ level: 0, key: 'root', label: 'All Warehouses' }])} />
        <KpiCard title="Total Capacity" subtitle="Bin + Manual" value={filteredKpis.totalCapacity} unit="Pallets" loading={loading} accent={P.neutral}
          onClick={() => setDrill([{ level: 0, key: 'root', label: 'All Warehouses' }])} />
        <KpiCard title="Occupied Capacity" subtitle="Physical + Floor + Manual" value={filteredKpis.usedCapacity} unit="Pallets" loading={loading} accent={P.occupied}
          onClick={() => setDrill([{ level: 0, key: 'root', label: 'All Warehouses' }])} />
        <KpiCard title="Sellable Capacity" subtitle="Free to sell now" value={filteredKpis.sellableCapacity} unit="Pallets" loading={loading} accent={P.sellable}
          onClick={() => setDrill([{ level: 0, key: 'root', label: 'All Warehouses' }])} />
        <KpiCard title="Committed Capacity" subtitle="Min. commitment per customer" value={filteredKpis.committedCapacity} unit="Pallets" loading={loading} accent={P.committed}
          onClick={() => setDrill([{ level: 0, key: 'root', label: 'All Warehouses' }])} />
        <KpiCard title="Shortfall Capacity" subtitle="Unfilled commitment gap" value={filteredKpis.shortfallCapacity} unit="Pallets" loading={loading} accent={'#dc2626'}
          onClick={() => setDrill([{ level: 0, key: 'root', label: 'All Warehouses' }])} />
      </div>

      {/* ╭─── Storage Category Pies (Cold / Freezer / Temp+Amb+Dry) ───╮ */}
      <ChartPanel title="Warehouse Storage Category" subtitle="Temperature Controlled · Cold · Chemical · Dangerous Goods · Freezer · Other — driven by your chamber icons">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          {areaCategoryBreakdown.map(cat => {
            const meta = AREA_CAT_META[cat.category] || AREA_CAT_META['Other Storage'];
            const pieData = [
              { name: 'Used',     value: cat.used,     fill: meta.color },
              { name: 'Sellable', value: cat.sellable, fill: meta.light },
            ];
            const accent = meta.color;
            const isExpanded = expandedCategory === cat.category;
            const ptRows = categoryPalletBreakdown[cat.category] || [];
            return (
              <div key={cat.category} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: accent, marginBottom: 4, letterSpacing: -0.2 }}>
                  {meta.icon} {cat.category}
                </div>
                <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setExpandedCategory(isExpanded ? null : cat.category)}>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius="55%" outerRadius="80%" paddingAngle={3} strokeWidth={0}>
                        {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Pie>
                      <RTooltip formatter={(v: any) => fmtNum(v) + ' pallets'} />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Center label */}
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: accent, lineHeight: 1 }}>{cat.util}%</div>
                    <div style={{ fontSize: 9, color: theme.textMuted, marginTop: 2, fontWeight: 600 }}>UTIL</div>
                  </div>
                </div>
                {/* Stats below pie */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginTop: 4 }}>
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: '6px 4px' }}>
                    <div style={{ fontSize: 8, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Capacity</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: theme.textPrimary, marginTop: 1 }}>{fmtNum(cat.capacity)}</div>
                  </div>
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: '6px 4px' }}>
                    <div style={{ fontSize: 8, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Used</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: accent, marginTop: 1 }}>{fmtNum(cat.used)}</div>
                  </div>
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: '6px 4px' }}>
                    <div style={{ fontSize: 8, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Sellable</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: P.sellable, marginTop: 1 }}>{fmtNum(cat.sellable)}</div>
                  </div>
                </div>
                <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 4, cursor: 'pointer' }}
                  onClick={() => setExpandedCategory(isExpanded ? null : cat.category)}>
                  {cat.areaCount} areas · {fmtNum(cat.bins)} bins {isExpanded ? '▲' : '▼ Click for pallet details'}
                </div>
                {/* Pallet type breakdown table */}
                {isExpanded && ptRows.length > 0 && (
                  <div style={{ marginTop: 8, background: '#f8fafc', borderRadius: 10, border: '1px solid #e5eaf2', overflow: 'hidden', textAlign: 'left' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr style={{ background: '#eef2f8' }}>
                          <th style={{ padding: '6px 8px', fontWeight: 700, color: theme.textSecondary, textAlign: 'left' }}>Pallet Type</th>
                          <th style={{ padding: '6px 8px', fontWeight: 700, color: theme.textSecondary, textAlign: 'right' }}>Used</th>
                          <th style={{ padding: '6px 8px', fontWeight: 700, color: theme.textSecondary, textAlign: 'right' }}>Free</th>
                          <th style={{ padding: '6px 8px', fontWeight: 700, color: theme.textSecondary, textAlign: 'right' }}>Util%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ptRows.map(pt => (
                          <tr key={pt.palletType} style={{ borderTop: '1px solid #eef2f8' }}>
                            <td style={{ padding: '5px 8px', fontWeight: 600, color: theme.textPrimary }}>{pt.palletType}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: accent, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(pt.used)}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: P.sellable, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(pt.free)}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: pt.util >= 80 ? '#dc2626' : pt.util >= 60 ? '#f59e0b' : '#10b981', fontVariantNumeric: 'tabular-nums' }}>{pt.util}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ChartPanel>

      {/* ╭─── Charts row 2: Area Detail (2fr) + Pallet Type (1fr) ───╮ */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 18 }}>
        <ChartPanel title="Capacity Breakdown by Area" subtitle="All storage areas — Used & Sellable with utilization %">
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={areaDetailBreakdown.slice(0, 15)} margin={{ top: 16, right: 24, left: 0, bottom: 6 }}>
              <CartesianGrid strokeDasharray="3 4" stroke="#eef2f8" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: theme.textSecondary, fontSize: 11, fontWeight: 600 }} interval={0} tickLine={false} axisLine={{ stroke: '#e5eaf2' }} angle={-25} textAnchor="end" height={50} />
              <YAxis yAxisId="left" tick={{ fill: theme.textMuted, fontSize: 11 }} tickFormatter={v => fmtNum(v)} tickLine={false} axisLine={false} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fill: theme.textMuted, fontSize: 11 }} tickFormatter={v => `${v}%`} tickLine={false} axisLine={false} />
              <RTooltip content={<PrettyTooltip />} cursor={{ fill: 'rgba(99,102,241,0.05)' }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
              <Bar yAxisId="left" dataKey="used" name="Occupied" stackId="a" fill="url(#gradSOcc)" radius={[0, 0, 0, 0]} />
              <Bar yAxisId="left" dataKey="sellable" name="Sellable" stackId="a" fill="url(#gradSSel)" radius={[6, 6, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="util" name="Util %" stroke={P.committed} strokeWidth={3} dot={{ r: 4, fill: '#fff', stroke: P.committed, strokeWidth: 2 }} activeDot={{ r: 6 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel title="Capacity by Pallet Type" subtitle="Used vs Sellable with utilization %">
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={palletTypeBreakdown} margin={{ top: 16, right: 24, left: 0, bottom: 6 }}>
              <CartesianGrid strokeDasharray="3 4" stroke="#eef2f8" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: theme.textSecondary, fontSize: 12, fontWeight: 600 }} interval={0} tickLine={false} axisLine={{ stroke: '#e5eaf2' }} />
              <YAxis yAxisId="left" tick={{ fill: theme.textMuted, fontSize: 11 }} tickFormatter={v => fmtNum(v)} tickLine={false} axisLine={false} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fill: theme.textMuted, fontSize: 11 }} tickFormatter={v => `${v}%`} tickLine={false} axisLine={false} />
              <RTooltip content={<PrettyTooltip />} cursor={{ fill: 'rgba(99,102,241,0.05)' }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
              <Bar yAxisId="left" dataKey="used" name="Used" stackId="a" fill="url(#gradSOcc)" radius={[0, 0, 0, 0]} />
              <Bar yAxisId="left" dataKey="sellable" name="Sellable" stackId="a" fill="url(#gradSSel)" radius={[6, 6, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="util" name="Util %" stroke={P.committed} strokeWidth={3} dot={{ r: 4, fill: '#fff', stroke: P.committed, strokeWidth: 2 }} activeDot={{ r: 6 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartPanel>
      </div>

      {/* ╭─── DRILL-OUT MODAL (fixed overlay) ───╮ */}
      {drill.length > 0 && drillData && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }} onClick={e => { if (e.target === e.currentTarget) setDrill([]); }}>
          <div style={{ width: '90%', maxWidth: 1100, maxHeight: '85vh', overflow: 'auto', borderRadius: 16 }}>
            <DrillPanel
              stack={drill} data={drillData} onDrill={drillDown} onUp={drillUp}
              onClose={() => setDrill([])} aliasR={aliasR} P={P}
            />
          </div>
        </div>
      )}

      {/* ╭─── 12-MONTH TREND + CLIENT PIE ───╮ */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 18 }}>
        <ChartPanel title="Capacity Trend — 12 Months" subtitle="Monthly capacity, usage, sellable & client count from WeeklyClientEntries">
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={monthlyTrend} margin={{ top: 16, right: 24, left: 0, bottom: 6 }}>
              <defs>
                <linearGradient id="gradTU" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={P.occupied} stopOpacity={0.25} /><stop offset="100%" stopColor={P.occupied} stopOpacity={0.03} /></linearGradient>
                <linearGradient id="gradTS" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={P.sellable} stopOpacity={0.25} /><stop offset="100%" stopColor={P.sellable} stopOpacity={0.03} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 4" stroke="#eef2f8" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: theme.textMuted, fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#e5eaf2' }} />
              <YAxis yAxisId="left" tick={{ fill: theme.textMuted, fontSize: 11 }} tickFormatter={v => fmtNum(v)} tickLine={false} axisLine={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: theme.textMuted, fontSize: 11 }} tickFormatter={v => `${v}%`} tickLine={false} axisLine={false} domain={[0, 100]} />
              <RTooltip content={<PrettyTooltip />} />
              <Area yAxisId="left" type="monotone" dataKey="capacity" name="Capacity" stroke="#94a3b8" fill="none" strokeWidth={2} strokeDasharray="5 3" />
              <Area yAxisId="left" type="monotone" dataKey="used" name="Used" stroke={P.occupied} fill="url(#gradTU)" strokeWidth={2} />
              <Area yAxisId="left" type="monotone" dataKey="sellable" name="Sellable" stroke={P.sellable} fill="url(#gradTS)" strokeWidth={2} />
              <Line yAxisId="right" type="monotone" dataKey="util" name="Util %" stroke={P.committed} strokeWidth={2.5} dot={{ r: 3, fill: '#fff', stroke: P.committed, strokeWidth: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
          <MiniLegend items={[{ color: '#94a3b8', label: 'Capacity', dashed: true }, { color: P.occupied, label: 'Used' }, { color: P.sellable, label: 'Sellable' }, { color: P.committed, label: 'Util %' }]} />
        </ChartPanel>

        <ChartPanel title="Client Market Share" subtitle={`${customerSummaries.length} clients`}>
          <ResponsiveContainer width="100%" height={340}>
            <PieChart>
              <Pie data={customerPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={105}
                paddingAngle={2} dataKey="value"
                label={({ name, percent }: { name?: string; percent?: number }) => `${(name||'').substring(0,10)} ${((percent||0)*100).toFixed(0)}%`}
              >
                {customerPieData.map((_, i) => <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />)}
              </Pie>
              <RTooltip content={<PrettyTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartPanel>
      </div>

      {/* ╭─── SQM ↔ PALLET CALCULATOR + FUTURE SPACE ───╮ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        {/* Calculator */}
        <ChartPanel title="SQM ↔ Pallet Converter" subtitle="Bidirectional conversion with unit economics">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0' }}>
            {/* Mode toggle */}
            <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 10, padding: 3, alignSelf: 'flex-start' }}>
              {(['sqm2pallet', 'pallet2sqm'] as const).map(m => (
                <button key={m} onClick={() => setCalcMode(m)} style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 700,
                  background: calcMode === m ? 'linear-gradient(135deg, #1e3a8a, #3b82f6)' : 'transparent',
                  color: calcMode === m ? '#fff' : theme.textSecondary, cursor: 'pointer', fontFamily: theme.fontFamily,
                }}>{m === 'sqm2pallet' ? 'SQM → Pallets' : 'Pallets → SQM'}</button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle}>{calcMode === 'sqm2pallet' ? 'Area (SQM)' : 'Number of Pallets'}</label>
                <input type="number" value={calcInput} onChange={e => setCalcInput(Math.max(0, Number(e.target.value)))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Pallet Type</label>
                <select value={calcPalletType} onChange={e => setCalcPalletType(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                  {Object.entries(PALLET_DB).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            {/* Result */}
            <div style={{ background: 'linear-gradient(135deg, #f0f4ff, #faf5ff)', borderRadius: 12, padding: '16px 18px', border: '1px solid #e0e7ff' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                <ResultBox label="Pallets" value={fmtNum(calcResult.pallets)} accent={P.neutral} />
                <ResultBox label="SQM" value={calcResult.sqm.toFixed(1)} accent={P.occupied} />
                <ResultBox label="SQFT" value={Math.round(calcResult.sqft).toLocaleString()} accent={P.sellable} />
                <ResultBox label="CBM" value={calcResult.cbm.toFixed(1)} accent="#7c3aed" />
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: theme.textMuted, textAlign: 'center' }}>
                1 pallet = {calcResult.sqmPer.toFixed(2)} SQM = {calcResult.sqftPer.toFixed(2)} SQFT · {PALLET_DB[calcPalletType]?.label}
              </div>
            </div>
          </div>
        </ChartPanel>

        {/* Future Space Planning */}
        <ChartPanel title="Future Space Forecast" subtitle="Projected capacity from churning & terminated clients">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '4px 0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              <ForecastCard label="Freed from Terminated" value={fmtNum(futureSpace.terminatedPallets)} sub="pallets" color="#94a3b8" />
              <ForecastCard label="At-Risk (30% est.)" value={fmtNum(futureSpace.atRiskPallets)} sub="pallets may free" color="#f59e0b" />
              <ForecastCard label="Total Projected SQM" value={fmtNum(futureSpace.freedSqm)} sub="SQM available" color="#7c3aed" />
            </div>
            {/* Status distribution bar */}
            <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: theme.textSecondary, marginBottom: 8 }}>Client Status Distribution</div>
              <div style={{ display: 'flex', height: 28, borderRadius: 8, overflow: 'hidden', gap: 2 }}>
                {Object.entries(statusCounts).filter(([,v]) => v > 0).map(([k, v]) => {
                  const total = customerSummaries.length || 1;
                  return (
                    <div key={k} style={{
                      flex: v / total, background: STATUS_META[k as CustStatus].color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700, color: '#fff', minWidth: 30,
                    }} title={`${STATUS_META[k as CustStatus].label}: ${v}`}>
                      {v}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <span key={k} style={{ fontSize: 10, color: theme.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: m.color, display: 'inline-block' }} />
                    {m.label} ({statusCounts[k as CustStatus] || 0})
                  </span>
                ))}
              </div>
            </div>
          </div>
        </ChartPanel>
      </div>

      {/* ╭─── AI CLIENT INTELLIGENCE TABLE ───╮ */}
      <ChartPanel title="AI Client Intelligence" subtitle={`${customerSummaries.length} clients · Scored by utilization, commitment, trend & revenue potential`}>
        {/* Column header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 90px 90px 90px 80px 60px 60px 130px',
          gap: 8, padding: '8px 12px', fontSize: 10, fontWeight: 700, color: theme.textMuted,
          textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '2px solid #eef2f8',
        }}>
          <span title="Click a client name to rename">Client ✎</span><span>Pallets</span><span>Committed</span>
          <span>Shortfall</span><span>SQM</span><span>Trend</span><span>Util</span><span>Status</span>
        </div>
        <div style={{ maxHeight: 520, overflowY: 'auto' }}>
          {(showAllClients ? customerSummaries : customerSummaries.slice(0, 12)).map((c, idx) => {
            const act = customerActivity[c.customer];
            const isSelected = selectedCustomer === c.customer;
            return (
            <React.Fragment key={c.customer}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 90px 90px 90px 80px 60px 60px 130px',
              gap: 8, padding: '10px 12px', alignItems: 'center',
              borderBottom: isSelected ? 'none' : '1px solid #f3f6fb', fontSize: 12,
              transition: 'background 0.15s', cursor: 'pointer',
              background: isSelected ? '#f0f5ff' : 'transparent',
            }}
              onClick={() => setSelectedCustomer(isSelected ? null : c.customer)}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#fafbfd'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
            >
              {/* Client name (click to rename) + recommendation */}
              <div style={{ minWidth: 0 }} onClick={e => e.stopPropagation()}>
                {editingName === c.customer ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      autoFocus
                      value={editNameVal}
                      onChange={e => setEditNameVal(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setEditingName(null); }}
                      onBlur={commitRename}
                      style={{
                        fontWeight: 700, fontSize: 12, color: theme.textPrimary,
                        border: '1px solid #3b82f6', borderRadius: 6, padding: '3px 8px',
                        outline: 'none', fontFamily: theme.fontFamily, width: '100%',
                        background: '#f0f5ff',
                      }}
                    />
                  </div>
                ) : (
                  <div
                    onClick={() => startRename(c.customer)}
                    title="Click to rename this customer"
                    style={{
                      fontWeight: 700, color: theme.textPrimary, whiteSpace: 'nowrap',
                      overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer',
                      borderBottom: '1px dashed transparent', transition: 'border-color 0.2s',
                    }}
                    onMouseEnter={e => { (e.target as HTMLElement).style.borderBottomColor = '#3b82f680'; }}
                    onMouseLeave={e => { (e.target as HTMLElement).style.borderBottomColor = 'transparent'; }}
                  >
                    {displayName(c.customer)}
                    {displayName(c.customer) !== c.customer && (
                      <span style={{ fontSize: 9, color: theme.textMuted, fontWeight: 500, marginLeft: 4 }}>
                        (code: {c.customer})
                      </span>
                    )}
                  </div>
                )}
                <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 2, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } as any}>
                  {c.recommendation}
                </div>
              </div>
              {/* Pallets */}
              <div style={{ fontWeight: 700, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(c.totalPallets)}</div>
              {/* Committed */}
              <div style={{ fontWeight: 600, color: c.committed > 0 ? P.committed : theme.textMuted, fontVariantNumeric: 'tabular-nums' }}>{c.committed > 0 ? fmtNum(c.committed) : '—'}</div>
              {/* Shortfall */}
              <div style={{ fontWeight: 600, color: c.shortfall > 0 ? '#ef4444' : theme.textMuted, fontVariantNumeric: 'tabular-nums' }}>{c.shortfall > 0 ? fmtNum(c.shortfall) : '—'}</div>
              {/* SQM */}
              <div style={{ fontVariantNumeric: 'tabular-nums', color: theme.textSecondary }}>{fmtNum(c.sqm)}</div>
              {/* Trend */}
              <div style={{ fontWeight: 600, color: c.trend > 0 ? '#10b981' : c.trend < 0 ? '#ef4444' : theme.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                {c.trend > 0 ? `+${c.trend}` : c.trend < 0 ? String(c.trend) : '—'}
              </div>
              {/* Util */}
              <div style={{ fontVariantNumeric: 'tabular-nums', color: theme.textSecondary }}>{c.committed > 0 ? `${c.utilization}%` : '—'}</div>
              {/* Status */}
              <div onClick={e => e.stopPropagation()}>
              <select value={c.status} onChange={e => setStatusOverrides(prev => ({ ...prev, [c.customer]: e.target.value as CustStatus }))}
                style={{
                  fontSize: 11, fontWeight: 600, padding: '4px 8px', borderRadius: 6,
                  border: `1px solid ${STATUS_META[c.status].color}40`,
                  background: `${STATUS_META[c.status].color}10`,
                  color: STATUS_META[c.status].color, cursor: 'pointer', fontFamily: theme.fontFamily,
                }}>
                {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.icon} {m.label}</option>)}
              </select>
              </div>
            </div>
            {/* ── Customer Activity Panel (Today / Week / Month In/Out) ── */}
            {isSelected && (
              <div style={{
                background: 'linear-gradient(135deg, #f0f5ff, #f8faff)', borderBottom: '2px solid #dbeafe',
                padding: '14px 16px', animation: 'fadeIn 0.2s ease',
              }}>
                {activityLoading && !act ? (
                  <div style={{ textAlign: 'center', padding: 12, fontSize: 12, color: theme.textMuted }}>Loading activity...</div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    {/* Today */}
                    <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #e5eaf2' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Today</div>
                      <div style={{ display: 'flex', gap: 16 }}>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: '#10b981', textTransform: 'uppercase', letterSpacing: 0.5 }}>Inbound</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#10b981', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(act?.inTodayPallets || 0)}</div>
                          <div style={{ fontSize: 9, color: theme.textMuted }}>{fmtNum(act?.inToday || 0)} qty</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 0.5 }}>Outbound</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#ef4444', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(act?.outTodayPallets || 0)}</div>
                          <div style={{ fontSize: 9, color: theme.textMuted }}>{fmtNum(act?.outToday || 0)} qty</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>Net</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                            {fmtNum((act?.inTodayPallets || 0) - (act?.outTodayPallets || 0))}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Last 7 Days */}
                    <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #e5eaf2' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Last 7 Days</div>
                      <div style={{ display: 'flex', gap: 16 }}>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: '#10b981', textTransform: 'uppercase', letterSpacing: 0.5 }}>Inbound</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#10b981', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(act?.inWeekPallets || 0)}</div>
                          <div style={{ fontSize: 9, color: theme.textMuted }}>{fmtNum(act?.inWeek || 0)} qty</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 0.5 }}>Outbound</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#ef4444', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(act?.outWeekPallets || 0)}</div>
                          <div style={{ fontSize: 9, color: theme.textMuted }}>{fmtNum(act?.outWeek || 0)} qty</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>Net</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                            {fmtNum((act?.inWeekPallets || 0) - (act?.outWeekPallets || 0))}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Last 30 Days */}
                    <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #e5eaf2' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Last 30 Days</div>
                      <div style={{ display: 'flex', gap: 16 }}>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: '#10b981', textTransform: 'uppercase', letterSpacing: 0.5 }}>Inbound</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#10b981', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(act?.inMonthPallets || 0)}</div>
                          <div style={{ fontSize: 9, color: theme.textMuted }}>{fmtNum(act?.inMonth || 0)} qty</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 0.5 }}>Outbound</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#ef4444', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(act?.outMonthPallets || 0)}</div>
                          <div style={{ fontSize: 9, color: theme.textMuted }}>{fmtNum(act?.outMonth || 0)} qty</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>Net</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                            {fmtNum((act?.inMonthPallets || 0) - (act?.outMonthPallets || 0))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {!act && !activityLoading && (
                  <div style={{ textAlign: 'center', padding: 8, fontSize: 11, color: theme.textMuted }}>No inbound/outbound activity found for this customer</div>
                )}
              </div>
            )}
            </React.Fragment>
            );
          })}
        </div>
        {customerSummaries.length > 12 && (
          <div style={{ padding: '12px 0', textAlign: 'center' }}>
            <button onClick={() => setShowAllClients(!showAllClients)} style={{
              padding: '8px 24px', borderRadius: 8, border: '1px solid #e5eaf2', background: '#fff',
              color: theme.textSecondary, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: theme.fontFamily,
            }}>{showAllClients ? 'Show Less' : `Show All ${customerSummaries.length} Clients`}</button>
          </div>
        )}
      </ChartPanel>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
 * DRILL-OUT PANEL — 4-level hierarchical drill
 * ═══════════════════════════════════════════════════════════════════════════ */
const DrillPanel: React.FC<{
  stack: DrillStack; data: any[]; onDrill: (key: string, label: string) => void;
  onUp: (level: number) => void; onClose: () => void; aliasR: any; P: any;
}> = ({ stack, data, onDrill, onUp, onClose, aliasR, P }) => {
  const level = stack.length;
  const levelLabels = ['Warehouses', 'Areas', 'Pallet Types', 'Bin Locations'];
  const totalCap = data.reduce((s: number, d: any) => s + (d.capacity || 0), 0);
  const totalUsed = data.reduce((s: number, d: any) => s + (d.used || 0), 0);

  return (
    <div style={{
      background: '#fff', border: '1px solid #e5eaf2', borderRadius: 16,
      boxShadow: '0 4px 24px rgba(20,40,80,0.1)', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 22px', borderBottom: '1px solid #eef2f8',
        background: 'linear-gradient(135deg, #f8faff, #eef4ff)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            padding: '4px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700,
            background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', color: '#fff',
          }}>Level {level} of 4</div>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            {stack.map((s, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span style={{ color: theme.textMuted, margin: '0 2px' }}>›</span>}
                <button onClick={() => onUp(i)} style={{
                  border: 'none', background: 'none', cursor: 'pointer', fontFamily: theme.fontFamily,
                  fontWeight: i === stack.length - 1 ? 700 : 500,
                  color: i === stack.length - 1 ? theme.textPrimary : theme.accents.blue,
                  fontSize: 13, padding: '2px 4px', borderRadius: 4,
                }}>{s.label}</button>
              </React.Fragment>
            ))}
            <span style={{ color: theme.textMuted, fontWeight: 500, marginLeft: 4 }}>· {levelLabels[level - 1] || ''}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: theme.textMuted }}>{data.length} items · {fmtNum(totalCap)} cap · {fmtNum(totalUsed)} used</span>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8, border: '1px solid #e5eaf2', background: '#fff',
            cursor: 'pointer', fontSize: 16, fontWeight: 700, color: theme.textSecondary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>
      </div>

      {/* Chart + Table */}
      <div style={{ padding: '16px 22px' }}>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={data.slice(0, 15)} margin={{ top: 8, right: 16, left: 0, bottom: 6 }}>
            <CartesianGrid strokeDasharray="3 4" stroke="#eef2f8" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: theme.textSecondary, fontSize: 11, fontWeight: 600 }} interval={0} tickLine={false} axisLine={{ stroke: '#e5eaf2' }} angle={data.length > 8 ? -30 : 0} textAnchor={data.length > 8 ? 'end' : 'middle'} />
            <YAxis tick={{ fill: theme.textMuted, fontSize: 11 }} tickFormatter={v => fmtNum(v)} tickLine={false} axisLine={false} />
            <RTooltip content={<PrettyTooltip />} cursor={{ fill: 'rgba(99,102,241,0.05)' }} />
            <Bar dataKey="used" name="Used" fill={`url(#gradSOcc)`} radius={[0, 0, 0, 0]} stackId="a" />
            <Bar dataKey="sellable" name="Sellable" fill={`url(#gradSSel)`} radius={[6, 6, 0, 0]} stackId="a" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
              {['#', 'Name', 'Capacity', 'Used', 'Sellable', 'Util %', ''].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '2px solid #eef2f8' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((d: any, i: number) => (
              <tr key={d.key} style={{ borderBottom: '1px solid #f3f6fb' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fafbfd'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                <td style={{ padding: '10px 14px', color: theme.textMuted, fontWeight: 600 }}>{i + 1}</td>
                <td style={{ padding: '10px 14px', fontWeight: 700, color: theme.textPrimary }}>{d.label}</td>
                <td style={{ padding: '10px 14px', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(d.capacity)}</td>
                <td style={{ padding: '10px 14px', fontVariantNumeric: 'tabular-nums', color: P.occupied, fontWeight: 600 }}>{fmtNum(d.used)}</td>
                <td style={{ padding: '10px 14px', fontVariantNumeric: 'tabular-nums', color: P.sellable, fontWeight: 600 }}>{fmtNum(d.sellable)}</td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 48, height: 6, borderRadius: 3, background: '#eef2f8', overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(100, d.util)}%`, height: '100%', borderRadius: 3, background: d.util > 85 ? '#ef4444' : d.util > 65 ? P.committed : P.sellable }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: theme.textSecondary }}>{d.util}%</span>
                  </div>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  {level < 4 && (
                    <button onClick={() => onDrill(d.key, d.label)} style={{
                      padding: '5px 12px', borderRadius: 6, border: '1px solid #e0e7ff',
                      background: '#f0f4ff', color: '#4338ca', fontWeight: 700, fontSize: 11,
                      cursor: 'pointer', fontFamily: theme.fontFamily,
                    }}>Drill ›</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ── Shared Sub-Components ────────────────────────────────────────────── */

const Divider = () => <div style={{ width: 1, alignSelf: 'stretch', background: '#eef2f8', margin: '4px 0' }} />;

const HeroStat: React.FC<{ label: string; value: string; tint: string }> = ({ label, value, tint }) => (
  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.1, opacity: 0.8, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: tint, letterSpacing: -0.4, lineHeight: 1.15, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);

const ChartPanel: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => {
  const [maximized, setMaximized] = useState(false);

  const panel = (
    <div style={{
      background: '#ffffff',
      border: maximized ? 'none' : '1px solid #e5eaf2',
      borderRadius: maximized ? 0 : 16,
      padding: maximized ? '28px 36px 24px' : '20px 22px 14px',
      boxShadow: maximized ? 'none' : '0 2px 10px rgba(20,40,80,0.05), 0 1px 2px rgba(20,40,80,0.03)',
      height: maximized ? '100%' : 'auto',
      display: maximized ? 'flex' : 'block',
      flexDirection: 'column' as const,
      overflow: maximized ? 'auto' : 'visible',
    }}>
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: maximized ? 20 : 15, fontWeight: 700, color: theme.textPrimary, letterSpacing: -0.2 }}>{title}</div>
          {subtitle && <div style={{ fontSize: maximized ? 13 : 11.5, color: theme.textMuted, marginTop: 3 }}>{subtitle}</div>}
        </div>
        <button
          onClick={() => setMaximized(m => !m)}
          title={maximized ? 'Minimize' : 'Maximize'}
          style={{
            background: 'none', border: '1px solid #d1d9e6', borderRadius: 6,
            cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: theme.textMuted, fontSize: 14, lineHeight: 1,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#f0f4fa')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
        >
          {maximized ? '✕' : '⛶'}
        </button>
      </div>
      <div style={maximized ? { flex: 1, minHeight: 0 } : {}}>
        {children}
      </div>
    </div>
  );

  if (!maximized) return panel;

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 9999, background: '#ffffff',
      overflow: 'auto',
    }}>
      {panel}
    </div>,
    document.body,
  );
};

const ResultBox: React.FC<{ label: string; value: string; accent: string }> = ({ label, value, accent }) => (
  <div style={{ background: '#fff', borderRadius: 8, padding: '10px 12px', border: '1px solid #e5eaf2', textAlign: 'center' }}>
    <div style={{ fontSize: 9, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 800, color: accent, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);

const ForecastCard: React.FC<{ label: string; value: string; sub: string; color: string }> = ({ label, value, sub, color }) => (
  <div style={{ background: `${color}08`, border: `1px solid ${color}25`, borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
    <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: theme.textPrimary, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 2 }}>{sub}</div>
  </div>
);

const MiniLegend: React.FC<{ items: { color: string; label: string; dashed?: boolean }[] }> = ({ items }) => (
  <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginTop: 8, paddingBottom: 4 }}>
    {items.map(it => (
      <div key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 500, color: theme.textSecondary }}>
        <span style={{ display: 'inline-block', width: 14, height: 3, borderRadius: 2, background: it.dashed ? 'none' : it.color, ...(it.dashed ? { backgroundImage: `repeating-linear-gradient(90deg, ${it.color} 0 4px, transparent 4px 7px)` } : {}) }} />
        {it.label}
      </div>
    ))}
  </div>
);

const PrettyTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5eaf2', borderRadius: 10, padding: '10px 14px', boxShadow: '0 8px 24px rgba(20,40,80,0.12)', fontSize: 12, minWidth: 160 }}>
      {label && <div style={{ fontSize: 11, fontWeight: 700, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #eef2f8' }}>{label}</div>}
      {payload.map((p: any, i: number) => {
        const isPct = p.name?.includes('%');
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, padding: '3px 0' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.textSecondary }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: p.color || p.payload?.color, display: 'inline-block' }} />
              {p.name}
            </span>
            <strong style={{ color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{isPct ? `${p.value}%` : fmtNum(Number(p.value))}</strong>
          </div>
        );
      })}
    </div>
  );
};

// Shared styles
const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: theme.textSecondary, display: 'block', marginBottom: 5 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', fontSize: 13, border: '1px solid #d8dee9', borderRadius: 8, fontFamily: theme.fontFamily, color: theme.textPrimary, background: '#fff', outline: 'none', boxSizing: 'border-box' };
const clearBtnStyle: React.CSSProperties = { padding: '7px 14px', borderRadius: 8, border: '1px solid #e5eaf2', background: '#fff', color: theme.textSecondary, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: theme.fontFamily };

export default SalesDashboard;
